import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Modal } from 'antd';

import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';
import { TILE_MAX_ELEMENTS } from '../../services/favorites';
import { normalizeId } from '../../utils/ids';
import { elementLabel } from '../items/itemValueDisplay';

/**
 * The elements a tile may show, in the card's order: every element of the
 * card (its inherited ones and its element sets' — the card-definitions
 * cache's merged list) except a file, which is pictures rather than a value,
 * and one the person's own card definition marks hidden from them (a
 * manager sees every element of the card they edit; the API refuses a value
 * hidden from them all the same).
 */
export function tileElementChoices(defs) {
	return (defs || []).filter(el => el && el.name && el.dataType !== 'file' && el.access !== 'hidden');
}

/**
 * "Pin as tile" (KPR-1127): pick one to three of a card's elements for a
 * record tile on Home — never typed, always picked. A fixed record's tile
 * (`target.source` 'record', its card `cardId`) or the newest record of a
 * card (`'latest'`, under the filter in `target`). Opened on a tile that is
 * already pinned, it shows its elements and saves the change, and offers
 * Unpin. The values are read on Home each time it loads.
 *
 * `target`: `{ source, targetId, collectionId, savedFilterId }` (services/
 * favorites pinTargetOf's shape). `name` says what is pinned ("Alpha",
 * "Newest Reading").
 */
export default function RecordTileModal({ open, onClose, target, cardId, name }) {
	const favorites = useOptionalFavorites();
	const cards = useOptionalCardDefinitions();
	const getElementsForCard = cards?.getElementsForCard;
	const choices = useMemo(
		() => tileElementChoices(typeof getElementsForCard === 'function' ? getElementsForCard(normalizeId(cardId)) : []),
		[getElementsForCard, cardId]
	);
	const current = open && favorites?.tileFor ? favorites.tileFor(target) : null;
	const existing = Array.isArray(current);
	const [picked, setPicked] = useState([]);
	const [busy, setBusy] = useState(false);

	// Opens on the tile as it is (its names that the card still offers), or none.
	const currentKey = existing ? current.join('\n') : '';
	useEffect(() => {
		if (!open) return;
		const offered = new Set(choices.map(el => el.name));
		setPicked(existing ? current.filter(nameOf => offered.has(nameOf)) : []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, currentKey, choices]);

	const full = picked.length >= TILE_MAX_ELEMENTS;
	// In the card's order, which is the order the tile draws them.
	const ordered = choices.map(el => el.name).filter(elName => picked.includes(elName));

	const save = async () => {
		if (!ordered.length || !favorites?.saveTile) return;
		setBusy(true);
		const ok = await favorites.saveTile(target, ordered);
		setBusy(false);
		if (ok) onClose();
	};
	const unpin = async () => {
		if (!favorites?.removeTile) return;
		setBusy(true);
		const ok = await favorites.removeTile(target);
		setBusy(false);
		if (ok) onClose();
	};

	return (
		<Modal
			open={open}
			title={existing ? 'Edit Home tile' : 'Pin as tile'}
			onCancel={onClose}
			destroyOnHidden
			className="record-tile-modal"
			footer={(
				<div className="record-tile-modal__footer">
					{existing ? (
						<Button type="link" onClick={unpin} disabled={busy}>Unpin from Home</Button>
					) : <span />}
					<span className="record-tile-modal__actions">
						<Button onClick={onClose} disabled={busy}>Cancel</Button>
						<Button type="primary" onClick={save} loading={busy} disabled={!ordered.length}>
							{existing ? 'Save' : 'Pin to Home'}
						</Button>
					</span>
				</div>
			)}
		>
			<p className="record-tile-modal__lead">
				{name ? <strong>{name}</strong> : null}
				{name ? ' — ' : null}
				Pick up to {TILE_MAX_ELEMENTS} values to show on your Home. They are read each time Home opens.
			</p>
			{choices.length ? (
				<Checkbox.Group
					className="record-tile-modal__choices"
					value={picked}
					onChange={values => setPicked(values.slice(0, TILE_MAX_ELEMENTS))}
					aria-label="Values to show"
				>
					{choices.map(el => (
						<Checkbox key={el.name} value={el.name} disabled={full && !picked.includes(el.name)}>
							{elementLabel(el) || el.name}
						</Checkbox>
					))}
				</Checkbox.Group>
			) : (
				<p className="record-tile-modal__none">This card has no values a tile can show.</p>
			)}
			{full ? <p className="record-tile-modal__hint">A tile shows at most {TILE_MAX_ELEMENTS} values.</p> : null}
		</Modal>
	);
}
